import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface OrganizationContextType {
  organizationId: number;
  setOrganizationId: (id: number) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const DEFAULT_CLIENT_ID = 0;
const STORAGE_KEY = 'selectedOrganizationId';

interface OrganizationProviderProps {
  children: ReactNode;
  defaultOrganizationId?: number;
}

export function OrganizationProvider({ children, defaultOrganizationId = DEFAULT_CLIENT_ID }: OrganizationProviderProps) {
  // Initialize with default value or from localStorage if available
  const [organizationId, setOrganizationIdState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? parseInt(stored, 10) : defaultOrganizationId;
    } catch {
      return defaultOrganizationId;
    }
  });

  // Persist to localStorage when organizationId changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, organizationId.toString());
    } catch (error) {
      console.warn('Failed to persist organizationId to localStorage:', error);
    }
  }, [organizationId]);

  // Memoize setOrganizationId to prevent unnecessary re-renders
  const setOrganizationId = useCallback((id: number) => {
    if (typeof id !== 'number' || isNaN(id)) {
      console.warn('Invalid organizationId provided:', id);
      return;
    }
    setOrganizationIdState(id);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue: OrganizationContextType = useMemo(() => ({ organizationId, setOrganizationId }), [organizationId, setOrganizationId]);

  return <OrganizationContext.Provider value={contextValue}>{children}</OrganizationContext.Provider>;
}

// oxlint-disable-next-line react/only-export-components
export function useOrganizationId(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganizationId must be used within a OrganizationProvider');
  }
  return context;
}
